import { useEffect, useState } from 'react';

/**
 * Custom hook that waits for Zywave shell to be ready
 * @returns boolean indicating if the shell is ready
 */
export function useZywaveShellReady(): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    const waitForShell = async () => {
      try {
        await customElements.whenDefined('zywave-shell');
        // Check if the operation was aborted before updating state
        if (!abortController.signal.aborted) {
          setIsReady(true);
        }
      } catch (error) {
        // Only handle non-abort errors
        if (error instanceof Error && error.name !== 'AbortError') {
          // Shell failed to load, component will remain in loading state
          // Error is handled at the component level
        }
      }
    };

    waitForShell();

    // Cleanup function to abort operation if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  return isReady;
}

/**
 * Custom hook that waits for Zywave API proxy to be ready
 * @param shellReady - whether the shell is ready (dependency)
 * @returns boolean indicating if the proxy is ready
 */
export function useZywaveProxyReady(shellReady: boolean): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!shellReady) return;

    const abortController = new AbortController();

    const waitForProxy = async () => {
      try {
        await customElements.whenDefined('zywave-api-proxy');
        // Check if the operation was aborted before updating state
        if (!abortController.signal.aborted) {
          setIsReady(true);
        }
      } catch (error) {
        // Only handle non-abort errors
        if (error instanceof Error && error.name !== 'AbortError') {
          // Proxy failed to load, component will remain in loading state
          // Error is handled at the component level
        }
      }
    };

    waitForProxy();

    // Cleanup function to abort operation if component unmounts
    return () => {
      abortController.abort();
    };
  }, [shellReady]);

  return isReady;
}
